import {
  Fragment,
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { AgGridReact } from 'ag-grid-react'
import { PlusCircleIcon } from '@heroicons/react/24/solid'
import { v4 as uuidv4 } from 'uuid'

import { AgGridActionCellRenderer } from '../components'

const DialogEditLearningLoungeItems = ({
  open = false,
  inProgress = false,
  title = '',
  text = '',
  yesText = 'Yes',
  noText = 'No',
  inProgressButtonText = 'Please wait...',
  rowData = [],
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  const gridRef = useRef()
  const [_rows, setRows] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const defaultColDef = useMemo(() => {
    return {
      editable: true,
      resizable: true,
    }
  }, [])

  // Store a local copy of `rowData`
  useEffect(() => {
    if (!open) {
      return
    }

    if (Array.isArray(rowData)) {
      setRows(rowData)
    }
  }, [rowData, open])

  const refreshCells = useCallback(() => {
    if (!open) {
      return
    }

    const params = {
      force: true,
      suppressFlash: true,
    }
    gridRef.current?.api?.refreshCells(params)
  }, [open])

  const getRowId = useCallback((params) => params.data.postId, [])

  // Auto resize columns based on their content
  const onFirstDataRendered = useCallback((e) => {
    let allColumnIds = []

    e.columnApi.getColumns().forEach((column) => {
      // Title, required, and thumbnail columns have a fixed width
      if (!['_required', '_title', '_thumbnail'].includes(column.getId())) {
        allColumnIds.push(column.getId())
      }
    })
    e.columnApi.autoSizeColumns(allColumnIds)
  }, [])

  const addRow = useCallback((params) => {
    const dummyRowData = generateDummyRowData()
    const atIndex = params ? params.rowIndex + 1 : 0
    const res = gridRef.current.api.applyTransaction({
      add: [dummyRowData],
      addIndex: atIndex,
    })

    // Turn on row editing after the animation is finished (~250ms)
    setTimeout(() => {
      gridRef.current.api.startEditingCell({
        rowIndex: res.add[0]?.rowIndex,
        // Place the cursor in the `_title` column
        colKey: '_title',
      })
    }, 250)
  }, [])

  const onRowDragEnd = useCallback(() => {
    refreshCells()
  }, [refreshCells])

  const onRowDataUpdated = useCallback(() => {
    refreshCells()

    // Update row count state
    setRowCount(gridRef.current?.api?.getDisplayedRowCount() ?? 0)
  }, [refreshCells])

  const handleRequiredChange = useCallback((e) => {
    let newRows = []
    gridRef.current.api.forEachNode((node) => {
      if (node.data.postId === e.target.id) {
        newRows.push({
          ...node.data,
          _required: e.target.checked,
        })
      } else {
        newRows.push({ ...node.data })
      }
    })
    setRows(newRows)
  }, [])

  const handleConfirm = () => {
    let newRowData = []
    gridRef.current.api.forEachNode((node) => {
      newRowData.push(node.data)
    })

    onConfirm([...newRowData])
  }

  const columnDefs = useMemo(
    () => [
      {
        field: 'action',
        editable: false,
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          return (
            <AgGridActionCellRenderer
              {...params}
              generateDummyRowData={generateDummyRowData}
              editColKey="_title"
            />
          )
        },
      },
      {
        field: '_required',
        headerName: 'Required',
        editable: false,
        width: 120,
        cellRenderer: (params) => {
          return (
            <input
              id={params.data.postId}
              type="checkbox"
              checked={params.data._required}
              onChange={handleRequiredChange}
            />
          )
        },
      },
      {
        field: '_title',
        headerName: 'Title',
        width: 300,
        cellRenderer: (params) => (
          <div
            className="overflow-x-hidden text-ellipsis whitespace-nowrap"
            dangerouslySetInnerHTML={{ __html: params.value }}
          />
        ),
      },
      {
        field: '_duration',
        headerName: 'Duration',
        cellRenderer: (params) => (
          <div
            className="overflow-x-hidden text-ellipsis whitespace-nowrap"
            dangerouslySetInnerHTML={{ __html: params.value }}
          />
        ),
      },
      {
        field: '_thumbnail',
        headerName: 'Thumbnail',
        width: 400,
        cellRenderer: (params) => (
          <div
            className="overflow-x-hidden text-ellipsis whitespace-nowrap"
            dangerouslySetInnerHTML={{ __html: params.value }}
          />
        ),
      },
    ],
    [handleRequiredChange]
  )

  return (
    <>
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={() => null}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={clsx(
                    'group w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-sm transition-all',
                    inProgress && 'inProgress'
                  )}
                >
                  <Dialog.Title
                    as="h3"
                    className="mb-5 text-xl font-bold leading-none text-black-olive"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <div
                      className="text-sm"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  </div>

                  {/** List */}
                  <div className="ag-theme-alpine relative h-[50vh] w-full">
                    <AgGridReact
                      ref={gridRef}
                      rowData={open ? _rows : []}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      editType={'fullRow'}
                      stopEditingWhenCellsLoseFocus={true}
                      animateRows={true}
                      rowDragManaged={true}
                      suppressNoRowsOverlay={true}
                      getRowId={getRowId}
                      onFirstDataRendered={onFirstDataRendered}
                      onRowDragEnd={onRowDragEnd}
                      onRowDataUpdated={onRowDataUpdated}
                    ></AgGridReact>
                    {rowCount === 0 && (
                      <div className="absolute left-1/2 top-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center backdrop-blur-sm">
                        <button
                          onClick={() => addRow()}
                          className="group/button flex items-center space-x-1 text-sm active:translate-y-[0.75px] active:scale-[0.99]"
                        >
                          <PlusCircleIcon className="h-[30px] w-[30px] fill-dark-charcoal/80 transition-all group-hover/button:fill-dark-charcoal/100" />
                          <span className="font-bold text-dark-charcoal/80 transition-all group-hover/button:text-dark-charcoal/100">
                            Click to add a new item
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 text-right">
                    <button
                      type="button"
                      className="rounded-full border-2 border-skin-button-accent bg-skin-button-accent px-6 py-2 text-xs leading-none text-white transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={handleConfirm}
                    >
                      {inProgress ? inProgressButtonText : yesText}
                    </button>
                    <button
                      type="button"
                      className="ml-2 rounded-full border-2 border-skin-button-accent px-6 py-2 text-xs leading-none transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:hover:bg-skin-button-accent group-[:not(.inProgress)]:hover:text-white group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={() => onCancel()}
                    >
                      {noText}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

const generateDummyRowData = () => {
  return {
    postId: uuidv4(),
    _required: false,
    _title: 'New Learning Lounge',
    _thumbnail: '',
    detail: {
      overview: '<p></p>',
      steps: [
        'Launch Percipio',
        'Complete the course',
        'Come back and complete the Knowledge Check for leaderboard points!',
      ],
      resources: [
        {
          id: uuidv4(),
          type: 'EXTERNAL',
          name: 'Launch Percipio',
          url: 'https://example.com',
        },
        {
          id: uuidv4(),
          type: 'EXTERNAL_ALT',
          name: 'Member Firm? Click here!',
          url: 'https://example.com',
        },
      ],
      questions: [
        {
          id: uuidv4(),
          statement: '<p></p>',
          choices: [
            {
              id: uuidv4(),
              correct: true,
              text: 'Choice 1',
            },
            {
              id: uuidv4(),
              correct: false,
              text: 'Choice 2',
            },
            {
              id: uuidv4(),
              correct: false,
              text: 'Choice 3',
            },
            {
              id: uuidv4(),
              correct: false,
              text: 'Choice 4',
            },
          ],
          point: 100,
          gradingMethod: 'GRADED_WITH_CONFIDENCE_SCORE',
          feedback: {
            correct: '<p>Enter something for the correct feedback...</p>',
            incorrect: '<p>Enter something for the incorrect feedback...</p>',
          },
        },
      ],
    },
  }
}

export default DialogEditLearningLoungeItems
